import { MarketingFormProps } from "@/slices/MarketingForm/utils";
import cn from "@/utils/cn";
import { PrismicNextImage } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";
import Image from "next/image";
import BlogBackgroundImage from "@/assets/blog-background.svg";
import HeroSearch from "./hero-search";
import StatefulStandaloneAuditForm from "./stateful-standalone-audit-form";
import TextComponent from "@/app/_components/TextComponent";

type HeroSectionProps = {
  heroTitle: MarketingFormProps["slice"]["primary"]["title"];
  heroDescription: MarketingFormProps["slice"]["primary"]["description"];
  backgroundImage: MarketingFormProps["slice"]["primary"]["background_image"]; // TODO: This should be a Prismic image type. Specifying it to Marketing potentially limits this.
  backgroundWithGradient?: boolean;
  // Search props for blog page
  showSearch?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
  searchDescription?: string;
  totalResults?: number;
  searchFocused?: boolean;
  displayAuditForm?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export default function HeroSection({
  heroTitle,
  heroDescription,
  backgroundImage,
  className,
  children,
  backgroundWithGradient = false,
  showSearch = false,
  searchQuery = "",
  onSearchChange,
  searchPlaceholder,
  searchDescription,
  totalResults,
  searchFocused,
  displayAuditForm = false,
  ...props
}: HeroSectionProps) {
  return (
    <div
      className={cn(
        "flex overflow-x-hidden flex-col  items-center relative ",
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "flex flex-col w-full justify-center items-center sbg-[#61A1FF] h-full xl:py-44  py-20 relative",
          backgroundWithGradient
            ? "bg-gradient-to-t from-[#4A9DFF] to-kd-lte-blue"
            : "",
          "min-w-[393px] md:min-w-[744px] lg:min-w-[1280px] h-[255px] lg:min-h-[393px]",
        )}
      >
        {/*  className="mt-[380px] px-5 md:mt-[300px] md:px-20 xl:mt-[400px]"  */}

        {!backgroundWithGradient ? (
          <>
            <PrismicNextImage
              className="absolute bottom-0 -z-10 size-full object-cover"
              field={backgroundImage}
              alt=""
              priority
            />
            <div className=" absolute bottom-0 size-full bg-[#00000033]"></div>
          </>
        ) : (
          <Image
            src={BlogBackgroundImage}
            alt="blog background"
            className="object-covers absolute -left-[120px] -top-5 bottom-0 -z-0 h-full  md:top-0 "
          />
        )}
        {!showSearch && (
          <>
            <div className="z-10 w-[70%] md:w-1/2">
              <PrismicRichText
                field={heroTitle}
                components={{
                  strong: ({ children }) => (
                    <div className="font-bold">{children}</div>
                  ),
                  paragraph: ({ children }) => {
                    return (
                      <TextComponent
                        type="h1"
                        className="text-center font-semibold leading-[36px] text-kd-white xl:leading-[56px]"
                      >
                        {children}
                      </TextComponent>
                    );
                  },
                }}
              />
            </div>
            <div className="z-10 my-6 w-[70%] md:w-1/2">
              <PrismicRichText
                field={heroDescription}
                components={{
                  strong: ({ children }) => (
                    <div className="font-semibold">{children}</div>
                  ),
                  paragraph: ({ children }) => {
                    return (
                      <TextComponent
                        type="h3"
                        className="text-center font-medium leading-[16px] text-white xl:leading-[27px]"
                      >
                        {children}
                      </TextComponent>
                    );
                  },
                }}
              />
            </div>
            {/* displayAuditForm */}
            {displayAuditForm && (
              <StatefulStandaloneAuditForm className="z-30 " />
            )}
          </>
        )}
        {/* Hero Search - only show if showSearch is true */}
        {showSearch && onSearchChange && (
          <div className="z-10 mt-8 w-full max-w-4xl">
            <HeroSearch
              searchQuery={searchQuery}
              onSearchChange={onSearchChange}
              placeholder={searchPlaceholder}
              description={searchDescription}
              totalResults={totalResults}
              initialFocus={searchFocused}
            />
          </div>
        )}
      </div>

      {children}
    </div>
  );
}
